import { useState, useEffect } from 'react'
import api from '../../utils/api'
import Button from '../ui/Button'
import Alert from '../ui/Alert'
import {
  FileText, Search, Upload, Monitor, Download,
  ToggleLeft, ToggleRight, Save,
} from 'lucide-react'

// ─── Editable fields whitelist ────────────────────────────────────────────────
const FIELD_OPTIONS = [
  { key: 'phone_primary',           label: 'رقم الهاتف الأساسي',          desc: 'يسمح لمقدم الطلب بتحديث رقم هاتفه الأساسي' },
  { key: 'phone_secondary',         label: 'رقم الهاتف الثانوي',          desc: 'رقم هاتف ثانوي بديل' },
  { key: 'contact_primary_phone',   label: 'هاتف جهة التواصل',            desc: 'رقم هاتف جهة التواصل' },
  { key: 'contact_secondary_phone', label: 'هاتف التواصل الثانوي',        desc: 'رقم ثانوي لجهة التواصل' },
  { key: 'contact_whatsapp',        label: 'رقم الواتساب',                desc: 'إضافة رقم واتساب' },
  { key: 'current_residence',       label: 'آخر مكان إقامة',              desc: 'تحديث مكان الإقامة' },
  { key: 'submitter_notes',         label: 'ملاحظات إضافية من المقدِّم',  desc: 'حقل نصي حر للمقدِّم' },
]

const STATUS_OPTIONS = [
  { value: '', label: 'جميع الحالات' },
  { value: 'submitted',        label: 'مقدَّم' },
  { value: 'pending review',   label: 'قيد المراجعة' },
  { value: 'documented',       label: 'موثَّق' },
  { value: 'rejected',         label: 'مرفوض' },
]

const TABS = [
  { id: 'form',     label: 'النموذج',       Icon: FileText  },
  { id: 'tracking', label: 'التتبع',        Icon: Search    },
  { id: 'bulk',     label: 'الاستيراد',     Icon: Upload    },
  { id: 'ui',       label: 'الواجهة',       Icon: Monitor   },
  { id: 'export',   label: 'تصدير البيانات', Icon: Download  },
]

// ─── Toggle switch component ──────────────────────────────────────────────────
function Toggle({ value, onChange, label, desc }) {
  return (
    <label className="flex items-start gap-4 cursor-pointer group">
      <button
        type="button"
        role="switch"
        aria-checked={value}
        onClick={() => onChange(!value)}
        className={`mt-0.5 flex-shrink-0 w-12 h-6 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 ${value ? 'bg-primary-600' : 'bg-stone-300'}`}
      >
        <span className={`block w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 mx-0.5 ${value ? 'translate-x-6' : 'translate-x-0'}`} />
      </button>
      <div>
        <p className="text-sm font-semibold text-stone-800">{label}</p>
        {desc && <p className="text-xs text-stone-400 mt-0.5">{desc}</p>}
      </div>
    </label>
  )
}

// ─── Section wrapper ──────────────────────────────────────────────────────────
function Section({ title, children }) {
  return (
    <div className="card mb-5">
      {title && <h3 className="text-sm font-bold text-stone-500 uppercase tracking-wide mb-4">{title}</h3>}
      <div className="space-y-5">{children}</div>
    </div>
  )
}

export default function SettingsPanel() {
  const [activeTab, setActiveTab] = useState('form')
  const [settings, setSettings]   = useState({
    form_enabled:        true,
    form_closed_message: '',
    form_notice:         '',
    success_message:     '',
    tracking_enabled:    true,
    editable_fields:     [],
    bulk_import_enabled: true,
    bulk_max_rows:       500,
    platform_name:       '',
    platform_tagline:    '',
  })
  const [loadError, setLoadError] = useState('')
  const [loading,   setLoading]   = useState(true)
  const [saving,    setSaving]    = useState(false)
  const [saveError, setSaveError] = useState('')
  const [saved,     setSaved]     = useState(false)

  // ── Export state ──────────────────────────────────────────────────────────
  const [exportFormat,   setExportFormat]   = useState('xlsx')
  const [exportStatus,   setExportStatus]   = useState('')
  const [exportRegion,   setExportRegion]   = useState('')
  const [exportFromDate, setExportFromDate] = useState('')
  const [exportToDate,   setExportToDate]   = useState('')
  const [exporting,      setExporting]      = useState(false)
  const [exportError,    setExportError]    = useState('')

  // ── Load all settings on mount ────────────────────────────────────────────
  useEffect(() => {
    api.get('admin/settings')
      .then(({ data }) => {
        setSettings(prev => ({ ...prev, ...data,
          form_closed_message: data.form_closed_message ?? '',
          form_notice:         data.form_notice         ?? '',
          success_message:     data.success_message     ?? '',
          platform_name:       data.platform_name       ?? '',
          platform_tagline:    data.platform_tagline     ?? '',
          editable_fields:     data.editable_fields      || [],
          bulk_max_rows:       data.bulk_max_rows         ?? 500,
        }))
      })
      .catch(err => setLoadError(err.message))
      .finally(() => setLoading(false))
  }, [])

  function set(key, value) {
    setSettings(prev => ({ ...prev, [key]: value }))
    setSaved(false)
  }

  function toggleField(key) {
    const cur = settings.editable_fields || []
    set('editable_fields', cur.includes(key) ? cur.filter(k => k !== key) : [...cur, key])
  }

  async function handleSave() {
    setSaveError('')
    setSaved(false)
    setSaving(true)
    try {
      await api.put('admin/settings', settings)
      // Invalidate public settings cache so changes take effect immediately
      try { sessionStorage.removeItem('_pub_settings') } catch {}
      setSaved(true)
    } catch (err) {
      setSaveError(err.message)
    } finally {
      setSaving(false)
    }
  }

  async function handleExport() {
    setExportError('')
    setExporting(true)
    try {
      const token = localStorage.getItem('admin_token') || sessionStorage.getItem('admin_token')
      const params = new URLSearchParams({ format: exportFormat })
      if (exportStatus)   params.set('status',    exportStatus)
      if (exportRegion)   params.set('region',    exportRegion)
      if (exportFromDate) params.set('from_date', exportFromDate)
      if (exportToDate)   params.set('to_date',   exportToDate)

      const res = await fetch(`/api/v1/admin/cases/export?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) {
        const j = await res.json().catch(() => ({}))
        throw new Error(j.error || `خطأ ${res.status}`)
      }
      const blob = await res.blob()
      const url  = URL.createObjectURL(blob)
      const a    = document.createElement('a')
      const ext  = exportFormat === 'xlsx' ? 'xlsx' : 'csv'
      a.href     = url
      a.download = `cases_${new Date().toISOString().slice(0,10)}.${ext}`
      a.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      setExportError(err.message)
    } finally {
      setExporting(false)
    }
  }

  if (loading) return <div className="text-stone-400 py-8 text-center">يتحمّل...</div>
  if (loadError) return <Alert type="error" message={loadError} />

  return (
    <div className="max-w-2xl">
      {/* Tab bar */}
      <div className="flex gap-1 mb-6 bg-stone-100 rounded-xl p-1 overflow-x-auto">
        {TABS.map(({ id, label, Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              activeTab === id
                ? 'bg-white text-primary-700 shadow-sm'
                : 'text-stone-500 hover:text-stone-800'
            }`}
          >
            <Icon size={15} />
            {label}
          </button>
        ))}
      </div>

      {/* ── Tab: النموذج ── */}
      {activeTab === 'form' && (
        <>
          <Section title="استقبال الطلبات">
            <Toggle
              value={!!settings.form_enabled}
              onChange={v => set('form_enabled', v)}
              label="تفعيل استقبال الطلبات"
              desc="عند التعطيل يُعرض للزوار رسالة الإغلاق بدلاً من النموذج"
            />
          </Section>

          <Section title="رسالة الإغلاق">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                الرسالة المعروضة عند إغلاق النموذج
              </label>
              <textarea
                rows={3}
                value={settings.form_closed_message}
                onChange={e => set('form_closed_message', e.target.value)}
                placeholder="نأسف، التسجيل مغلق حالياً. سيعود قريباً."
                className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 resize-none"
              />
            </div>
          </Section>

          <Section title="تنبيه أعلى النموذج">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                رسالة إعلامية تظهر أعلى النموذج (اتركها فارغة لإخفائها)
              </label>
              <textarea
                rows={3}
                value={settings.form_notice}
                onChange={e => set('form_notice', e.target.value)}
                placeholder="مثال: الآن نستقبل حالات منطقة ريف دمشق فقط"
                className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 resize-none"
              />
            </div>
          </Section>

          <Section title="رسالة صفحة النجاح">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                رسالة مخصصة تظهر في صفحة تأكيد التقديم (اتركها فارغة للافتراضي)
              </label>
              <textarea
                rows={3}
                value={settings.success_message}
                onChange={e => set('success_message', e.target.value)}
                placeholder="شكراً لمساهمتك في توثيق الشهداء والمفقودين."
                className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 resize-none"
              />
            </div>
          </Section>
        </>
      )}

      {/* ── Tab: التتبع ── */}
      {activeTab === 'tracking' && (
        <>
          <Section title="صفحة المتابعة">
            <Toggle
              value={!!settings.tracking_enabled}
              onChange={v => set('tracking_enabled', v)}
              label="تفعيل صفحة متابعة الطلب"
              desc="يتيح للمواطنين البحث عن طلبهم بالرقم المرجعي ورقم الهاتف"
            />
          </Section>

          <Section title="الحقول القابلة للتعديل">
            <p className="text-xs text-stone-400 -mt-2">
              الحقول التي يسمح للمواطن بتحديثها بعد التقديم
            </p>
            {FIELD_OPTIONS.map(({ key, label, desc }) => (
              <label key={key} className="flex items-start gap-3 cursor-pointer hover:bg-stone-50 rounded-lg p-2 -mx-2 transition-colors">
                <input
                  type="checkbox"
                  checked={(settings.editable_fields || []).includes(key)}
                  onChange={() => toggleField(key)}
                  className="mt-0.5 h-4 w-4 accent-primary-600 flex-shrink-0"
                />
                <div>
                  <p className="text-sm font-semibold text-stone-800">{label}</p>
                  <p className="text-xs text-stone-400 mt-0.5">{desc}</p>
                </div>
              </label>
            ))}
          </Section>
        </>
      )}

      {/* ── Tab: الاستيراد ── */}
      {activeTab === 'bulk' && (
        <>
          <Section title="الاستيراد الجماعي">
            <Toggle
              value={!!settings.bulk_import_enabled}
              onChange={v => set('bulk_import_enabled', v)}
              label="تفعيل ميزة الاستيراد الجماعي"
              desc="يتيح لحسابات المنظمات رفع ملفات Excel لإدخال حالات متعددة"
            />
          </Section>

          <Section title="قيود الاستيراد">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                الحد الأقصى لعدد الصفوف في كل ملف
              </label>
              <input
                type="number"
                min={1}
                max={5000}
                value={settings.bulk_max_rows}
                onChange={e => set('bulk_max_rows', Math.max(1, Math.min(5000, +e.target.value || 500)))}
                className="w-32 border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
              <p className="text-xs text-stone-400 mt-1">بين 1 و 5000 صف (الافتراضي: 500)</p>
            </div>
          </Section>
        </>
      )}

      {/* ── Tab: الواجهة ── */}
      {activeTab === 'ui' && (
        <Section title="هوية المنصة">
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">اسم المنصة</label>
            <input
              type="text"
              value={settings.platform_name}
              onChange={e => set('platform_name', e.target.value)}
              placeholder="المنصة الوطنية لتوثيق شهداء ومفقودي الثورة السورية المباركة"
              className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">الشعار النصي</label>
            <input
              type="text"
              value={settings.platform_tagline}
              onChange={e => set('platform_tagline', e.target.value)}
              placeholder="حفظاً للذاكرة، وإحقاقاً للحق"
              className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>
        </Section>
      )}

      {/* ── Tab: تصدير البيانات ── */}
      {activeTab === 'export' && (
        <Section title="تصدير الحالات">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">صيغة الملف</label>
              <select
                value={exportFormat}
                onChange={e => setExportFormat(e.target.value)}
                className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
              >
                <option value="xlsx">Excel (.xlsx)</option>
                <option value="csv">CSV (.csv)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">تصفية بالحالة</label>
              <select
                value={exportStatus}
                onChange={e => setExportStatus(e.target.value)}
                className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
              >
                {STATUS_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">من تاريخ</label>
              <input
                type="date"
                value={exportFromDate}
                onChange={e => setExportFromDate(e.target.value)}
                className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">إلى تاريخ</label>
              <input
                type="date"
                value={exportToDate}
                onChange={e => setExportToDate(e.target.value)}
                className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">تصفية بالمنطقة (اختياري)</label>
            <input
              type="text"
              value={exportRegion}
              onChange={e => setExportRegion(e.target.value)}
              placeholder="مثال: دمشق"
              className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>

          {exportError && <Alert type="error" message={exportError} />}

          <Button
            variant="primary"
            onClick={handleExport}
            loading={exporting}
            className="w-full flex items-center justify-center gap-2"
          >
            <Download size={16} />
            تنزيل الملف
          </Button>

          <p className="text-xs text-stone-400 text-center">
            يتم تصدير ما يصل الى 5000 حالة. استخدم الفلاتر لتضييق النتائج.
          </p>
        </Section>
      )}

      {/* ── Save button (not shown on export tab) ── */}
      {activeTab !== 'export' && (
        <div className="sticky bottom-4">
          {saveError && <Alert type="error" message={saveError} className="mb-3" />}
          {saved     && <Alert type="success" message="تم حفظ الإعدادات بنجاح" className="mb-3" />}
          <Button
            variant="primary"
            onClick={handleSave}
            loading={saving}
            className="w-full flex items-center justify-center gap-2"
          >
            <Save size={16} />
            حفظ الإعدادات
          </Button>
        </div>
      )}
    </div>
  )
}

